// Kataloğa yükselt penceresinin (PromoteDialog) saf çekirdeği — DOM yok, testle kilitli:
// form durumu → istek gövdesi, 400 ms gecikmeli önizleme zamanlayıcısı (jetonla sıra dışı yanıtı
// eler — DR9), engel sınıflandırması (panel / alan iletisi), 409 `{metricId}` → satır hatası,
// paket ↔ kategori iki yönlü öneri (DR19; ikisi de kullanıcıca değiştirilebilir), zincir bölme
// (kök başlıkta, kırılım grubu/üyesi sahibinin altında özet satırı).

import {
  KPI_PROMOTE_PARENT_PROBLEMS,
  type KpiPromoteBody,
  type KpiPromoteParentProblem,
  type KpiPromotePreview,
} from "@mahir/shared";
import { KPI_PACKAGES_COPY } from "./kpi-packages-copy";

const P = KPI_PACKAGES_COPY.promote;

export const PROMOTE_CHECK_DELAY_MS = 400;

export interface PromoteChainOverride {
  label?: string;
  newCode?: string;
  parentId?: string;
}

export interface PromoteFormState {
  rootId: string;
  label: string;
  newCode: string;
  /** kökün varsayılan kodu — boş "Yeni kod" girdisinin yer tutucusu ve gönderilen değeri */
  defaultCode: string;
  parentId: string;
  packageIds: string[];
  chainOverrides: Record<string, PromoteChainOverride>;
  /** kullanıcı elle dokundu mu — öneri yalnız dokunulmamış (boş) alana yazılır */
  touched: { parent: boolean; packages: boolean };
}

export type PromoteChainRow = KpiPromotePreview["chain"][number];

export function initialPromoteForm(root: {
  id: string;
  label: string;
  code: string;
}): PromoteFormState {
  return {
    rootId: root.id,
    label: root.label,
    newCode: defaultPromotedCode(root.code),
    defaultCode: defaultPromotedCode(root.code),
    parentId: "",
    packageIds: [],
    chainOverrides: {},
    touched: { parent: false, packages: false },
  };
}

/**
 * Form → `POST /kpi/metrics/:id/promote` gövdesi; boş etiket gönderilmez (sunucu mevcut adı korur).
 * Boş "Yeni kod" girdisi ekrandaki yer tutucuyla (varsayılan kod) AÇIKÇA gönderilir — gövde
 * kullanıcının gördüğünden sessizce farklılaşmaz. `defaults`: satır metricId → varsayılan kod.
 */
export function toPromoteBody(
  state: PromoteFormState,
  dryRun: boolean,
  defaults: ReadonlyMap<string, string> = new Map(),
): KpiPromoteBody {
  const label = state.label.trim();
  const newCode = state.newCode.trim() || state.defaultCode;
  const chain: NonNullable<KpiPromoteBody["chain"]> = [];
  for (const [metricId, o] of Object.entries(state.chainOverrides)) {
    if (metricId === state.rootId) continue;
    const item: NonNullable<KpiPromoteBody["chain"]>[number] = { metricId };
    if (o.label?.trim()) item.label = o.label.trim();
    if (o.newCode !== undefined) {
      const code = o.newCode.trim() || defaults.get(metricId);
      if (code) item.newCode = code;
    }
    if (o.parentId) item.parentId = o.parentId;
    if (Object.keys(item).length > 1) chain.push(item);
  }
  return {
    ...(label ? { label } : {}),
    ...(newCode ? { newCode } : {}),
    ...(state.parentId ? { parentId: state.parentId } : {}),
    ...(state.packageIds.length > 0 ? { packageIds: state.packageIds } : {}),
    ...(chain.length > 0 ? { chain } : {}),
    ...(dryRun ? { dryRun: true } : {}),
  };
}

export interface CheckScheduler {
  /** Yeni denetim planlar; dönen jeton yanıt geldiğinde `isCurrent` ile sınanır. */
  schedule: (delayMs?: number) => number;
  isCurrent: (token: number) => boolean;
  cancel: () => void;
}

/**
 * Gecikmeli önizleme: her değişiklik önceki zamanlayıcıyı iptal eder ve jetonu ilerletir; uçuştaki
 * eski isteğin yanıtı (jetonu artık güncel değil) yok sayılır — sıra dışı sonuç ekrana yazılmaz.
 */
export function createCheckScheduler(
  run: (token: number) => void,
  delayMs = PROMOTE_CHECK_DELAY_MS,
): CheckScheduler {
  let token = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const clear = (): void => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  return {
    schedule(ms = delayMs) {
      clear();
      token += 1;
      const t = token;
      timer = setTimeout(() => {
        timer = null;
        run(t);
      }, ms);
      return t;
    },
    isCurrent: (t) => t === token,
    cancel() {
      clear();
      token += 1;
    },
  };
}

interface ApiErrorLike {
  status: number;
  code: string;
  message: string;
  details?: unknown;
}

export function isApiErrorLike(x: unknown): x is ApiErrorLike {
  const e = x as Partial<ApiErrorLike> | null;
  return !!e && typeof e === "object" && typeof e.code === "string" && typeof e.status === "number";
}

const detailsOf = (e: ApiErrorLike): Record<string, unknown> =>
  e.details && typeof e.details === "object" ? (e.details as Record<string, unknown>) : {};

const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

export type PromoteBlockerKind =
  | "parent"
  | "tooLarge"
  | "memberConflict"
  | "clientChildren"
  | "clientRefs"
  | "forbidden";

export interface PromoteBlocker {
  kind: PromoteBlockerKind;
  /** true → tablo yerine engel paneli (`role=alert`); false → üst kategori alanının iletisi */
  panel: boolean;
  text: string;
  rows?: string[];
  codes?: string[];
}

function parentBlocker(
  problem: KpiPromoteParentProblem,
  hasGlobalCategory: boolean,
): PromoteBlocker {
  if (!hasGlobalCategory) return { kind: "parent", panel: true, text: P.noCategories };
  return {
    kind: "parent",
    panel: false,
    text: problem === "missing" ? P.parentRequired : P.parentProblem[problem],
  };
}

const isParentProblem = (v: unknown): v is KpiPromoteParentProblem =>
  (KPI_PROMOTE_PARENT_PROBLEMS as readonly unknown[]).includes(v);

/**
 * Önizleme ya da hata → engel. `null` = engel yok (satır hataları `rowErrors`'ta, diğer hatalar
 * genel hata satırında). Kategori hiç yoksa üst sorunu panele çıkar (Sistem Yöneticisi açmalı).
 */
export function classifyBlockers(
  /** önizleme (`KpiPromotePreview`) ya da yakalanan hata */
  source: unknown,
  opts: { hasGlobalCategory: boolean },
): PromoteBlocker | null {
  if (!isApiErrorLike(source)) {
    const p = source as KpiPromotePreview | null | undefined;
    return p?.parentProblem ? parentBlocker(p.parentProblem, opts.hasGlobalCategory) : null;
  }
  const d = detailsOf(source);
  switch (source.code) {
    case "KPI_INVALID_STATE":
      return isParentProblem(d.reason) ? parentBlocker(d.reason, opts.hasGlobalCategory) : null;
    case "KPI_PROMOTE_CHAIN_TOO_LARGE":
      return { kind: "tooLarge", panel: true, text: P.chainTooLarge };
    case "KPI_PROMOTE_MEMBER_CONFLICT":
      return {
        kind: "memberConflict",
        panel: true,
        text: P.memberConflict,
        rows: typeof d.metricId === "string" ? [d.metricId] : [],
      };
    case "KPI_PROMOTE_HAS_CLIENT_CHILDREN":
      return {
        kind: "clientChildren",
        panel: true,
        text: P.clientChildren,
        rows: strings(d.metricIds),
      };
    case "KPI_PROMOTE_FORMULA_CLIENT_REFS":
      return { kind: "clientRefs", panel: true, text: P.clientRefs, codes: strings(d.codes) };
    case "forbidden":
      return source.status === 403 ? { kind: "forbidden", panel: true, text: P.forbidden } : null;
    default:
      return null;
  }
}

/** Satır hataları: kod çakışması (409 `{metricId}`) / geçersiz kod (`{metricId}`) → o satır. */
export function rowErrors(error: unknown): Map<string, string> {
  const out = new Map<string, string>();
  if (!isApiErrorLike(error)) return out;
  const id = detailsOf(error).metricId;
  if (typeof id !== "string") return out;
  if (error.code === "KPI_METRIC_CODE_TAKEN") out.set(id, P.codeTaken);
  else if (error.code === "KPI_FORMULA_INVALID") out.set(id, error.message);
  return out;
}

/** 409 ayrıntısındaki zincir (pencere tabloyu yine çizer ve hatalı satıra odaklanır — DR9). */
export function errorChain(error: unknown): PromoteChainRow[] | null {
  if (!isApiErrorLike(error)) return null;
  const c = detailsOf(error).chain;
  return Array.isArray(c) ? (c as PromoteChainRow[]) : null;
}

/**
 * DR19: paket işaretli + üst boş (dokunulmamış) → önerilen kategori; kategori seçili + paket yok
 * (dokunulmamış) → önerilen paketler. Değişiklik yoksa aynı nesne döner (gereksiz denetim yok).
 */
export function applySuggestions(
  state: PromoteFormState,
  preview: KpiPromotePreview,
): PromoteFormState {
  let next = state;
  if (state.parentId === "" && !state.touched.parent && preview.suggestedParentId) {
    next = { ...next, parentId: preview.suggestedParentId };
  }
  if (
    state.packageIds.length === 0 &&
    !state.touched.packages &&
    preview.suggestedPackageIds.length > 0
  ) {
    next = { ...next, packageIds: [...preview.suggestedPackageIds] };
  }
  return next;
}

export function nameWarningFor(
  metricId: string,
  preview: KpiPromotePreview | null | undefined,
): string | null {
  return preview?.nameWarnings.some((w) => w.metricId === metricId) ? P.customerName : null;
}

export function confirmText(n: number): string {
  return P.confirm(n);
}

export interface SplitChain {
  root: PromoteChainRow | null;
  /** zorunlu bağımlılıklar (kök ve kırılım satırları hariç) — tablo satırları */
  deps: PromoteChainRow[];
  /** sahip (kök ya da bağımlılık) → altındaki kırılım ÜYESİ sayısı (grup satırları sayılmaz) */
  breakdownCount: Map<string, number>;
  /** kırılım satırı (grup ya da üye) → sahibi (kök ya da bağımlılık) */
  ownerOf: Map<string, string>;
  /** yükselecek tüm gösterge sayısı (onay metni) */
  total: number;
}

/**
 * Kırılım satırı = üstü zincirde başka bir satır olan satır (grup → toplam, üye → grup); bağımlılık
 * satırlarının üstü seçilen kategoridir. Sahip, üst zinciri kırılım dışı satıra varana dek yürünür.
 * Üye = üstü de bir kırılım satırı (grup) olan kırılım satırı.
 */
export function splitChain(chain: readonly PromoteChainRow[], rootId: string): SplitChain {
  const byId = new Map(chain.map((r) => [r.metricId, r]));
  const isBreakdown = (r: PromoteChainRow): boolean =>
    r.metricId !== rootId && r.parentId !== null && byId.has(r.parentId);
  const breakdownCount = new Map<string, number>();
  const ownerOf = new Map<string, string>();
  for (const r of chain) {
    if (!isBreakdown(r)) continue;
    let owner = r;
    for (let i = 0; i < chain.length && isBreakdown(owner); i += 1)
      owner = byId.get(owner.parentId!)!;
    ownerOf.set(r.metricId, owner.metricId);
    const parent = byId.get(r.parentId!)!;
    if (isBreakdown(parent)) {
      breakdownCount.set(owner.metricId, (breakdownCount.get(owner.metricId) ?? 0) + 1);
    }
  }
  return {
    root: byId.get(rootId) ?? null,
    deps: chain.filter((r) => r.metricId !== rootId && !isBreakdown(r)),
    breakdownCount,
    ownerOf,
    total: chain.length,
  };
}

/** Satır hatasının yeri: kod girdisi (kök/bağımlılık) ya da sahibin kırılım özet satırı. */
export interface PlacedRowErrors {
  /** kök ya da bağımlılık → metin (kod girdisinde) */
  rows: Map<string, string>;
  /** sahip → metin (kırılım grubu/üyesi hatası, sahibin özet satırında) */
  breakdown: Map<string, string>;
  /** hiçbir çizilen satıra düşmeyen hatalar → genel hata satırı (sessiz pasif durum yok) */
  unplaced: string[];
  /** "satıra git" sırası: kök → kökün kırılımı → her bağımlılık → onun kırılımı */
  order: { kind: "row" | "breakdown"; id: string }[];
}

/** Aynı sahip satırda birden çok hata: hepsi listelenir (satır sonuyla ayrılır — `pre-line`). */
function appendMessage(map: Map<string, string>, key: string, text: string): void {
  const prev = map.get(key);
  if (prev === undefined) map.set(key, text);
  else if (!prev.split("\n").includes(text)) map.set(key, `${prev}\n${text}`);
}

/**
 * Önizlemedeki zincir üyesi (kök hariç — kök, üst kategori alanında) üst sorunları: metricId → metin.
 * Eski sunucu yanıtında `parentProblems` yoksa boş (engel paneli/alan iletisi yine çalışır).
 */
export function parentProblemMessages(
  preview: KpiPromotePreview | null | undefined,
  rootId: string,
): Map<string, string> {
  const out = new Map<string, string>();
  for (const pp of preview?.parentProblems ?? []) {
    if (pp.metricId === rootId) continue;
    out.set(pp.metricId, pp.problem === "missing" ? P.parentRequired : P.parentProblem[pp.problem]);
  }
  return out;
}

/** İki hata kaynağını (kod/çakışma hataları + üst sorunları) satır başına birleştirir. */
export function mergeRowMessages(...sources: ReadonlyMap<string, string>[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const src of sources) for (const [id, text] of src) appendMessage(out, id, text);
  return out;
}

export function placeRowErrors(
  errors: ReadonlyMap<string, string>,
  rootId: string,
  chain: readonly PromoteChainRow[],
): PlacedRowErrors {
  const split = splitChain(chain, rootId);
  const visible = new Set([rootId, ...split.deps.map((d) => d.metricId)]);
  const rows = new Map<string, string>();
  const breakdown = new Map<string, string>();
  const unplaced: string[] = [];
  for (const [id, text] of errors) {
    const owner = split.ownerOf.get(id);
    if (visible.has(id)) appendMessage(rows, id, text);
    else if (owner !== undefined && visible.has(owner)) appendMessage(breakdown, owner, text);
    else unplaced.push(text);
  }
  const order: PlacedRowErrors["order"] = [];
  for (const id of visible) {
    if (rows.has(id)) order.push({ kind: "row", id });
    if (breakdown.has(id)) order.push({ kind: "breakdown", id });
  }
  return { rows, breakdown, unplaced, order };
}

/** Sunucu varsayılanıyla aynı: müşteri kod öneki `c_` düşer (yer tutucu + boş girdi gövdesi). */
export function defaultPromotedCode(code: string): string {
  return code.replace(/^c_/, "");
}

/** Zincir satırlarının varsayılan kodları (boş kod girdisi bunu gönderir). */
export function chainDefaultCodes(chain: readonly PromoteChainRow[]): Map<string, string> {
  return new Map(chain.map((r) => [r.metricId, defaultPromotedCode(r.code)]));
}
